import { useTranslation } from 'react-i18next'
import { Button, Spin, Table, Tooltip } from 'antd'
import { ClearOutlined, FilterOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useEffect, useCallback } from 'react'
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom'

import { useCarriers, useContracts } from '@hooks'
import { Carrier, ContractFilters, PATHS, RoleEnum } from '@types'
import { useContractContracts, useGeneral, useUser } from '@contexts'

import { dispatchCustomFilterEvent } from '../../utils/filterEvents'
import { ContentContainer, StyledButton, StyledRow, StyledSelect } from '../StyledComponents'
import { useContractPagination } from './hooks/useContractsPagination'
import arrayToOptions from '../../utils/array-to-options'
import { initialContractsFiltersState } from '../../contexts/ContractsContext'
import FormatPrice from '../FormatPrice'

const ContractsDashboard = () => {
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { allCarriersOptions } = useCarriers()
  const { filters, setFilters, isContractsSync, resetFilters } = useContractContracts()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { state } = useUser()
  const roles = state?.roles.map(role => role.name)
  const { ADMINRO, PROCUREMENT } = RoleEnum
  const {
    contracts,
    contractsIsLoading,
    contractsFilters,
    contractsFiltersIsLoading,
    asnums,
    asnumsIsLoading,
  } = useContracts(filters)

  const clearAllFilters = useCallback(() => {
    setFilters(initialContractsFiltersState.filters)
  }, [setFilters])

  const handleContractFilterChange = useCallback(
    (value: any) => {
      setFilters((prevState: ContractFilters) => ({
        ...prevState,
        page: 1,
        contract: value as string,
      }))
    },
    [setFilters],
  )

  useEffect(() => {
    const contractNumber = searchParams.get('contractNumber')
    if (contractNumber) {
      handleContractFilterChange(contractNumber)
    }
  }, [searchParams, handleContractFilterChange])

  useEffect(() => {
    const service = searchParams.get('service')
    if (service) {
      setFilters((prevState: ContractFilters) => ({
        ...prevState,
        page: 1,
        asnum: service,
      }))
    }
  }, [searchParams, setFilters])

  const handleLinkClick = (path: string, event: React.MouseEvent) => {
    event.preventDefault()
    navigate(path)
  }

  useEffect(() => {
    const handleContractFilter = (event: CustomEvent) => {
      handleContractFilterChange(event.detail)
    }

    window.addEventListener('contractFilter', handleContractFilter as EventListener)
    return () => {
      window.removeEventListener('contractFilter', handleContractFilter as EventListener)
    }
  }, [handleContractFilterChange])

  const pagination = useContractPagination({ filters, setFilters }, contracts?.total)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const getRoutePath = (asnumId: string) => {
    let base = PATHS.ADMIN.ROUTES
    if (roles?.includes(ADMINRO)) base = PATHS.ADMINRO.ROUTES
    else if (roles?.includes(PROCUREMENT)) base = PATHS.PROCUREMENT.ROUTES
    return `${base}?service=${asnumId}`
  }

  const handleAsnumClick = (asnumId: string, e: React.MouseEvent) => {
    e.preventDefault()
    const path = getRoutePath(asnumId)
    handleLinkClick(path, e)
    dispatchCustomFilterEvent('routeFilter', asnumId)
  }

  const columns = [
    {
      title: t('contract_dashboard.contract'),
      dataIndex: 'contract',
      key: 'contract',
      render: (contract: string, record: any) => (
        <Link
          to={`${location.pathname}?contractNumber=${contract}`}
          style={{ display: 'block', color: '#1890ff' }}
          onClick={e => {
            e.preventDefault()
            handleContractFilterChange(contract)
            navigate(`${location.pathname}?contractNumber=${contract}`)
          }}
        >
          {record.descriptionContract ? `${contract} - ${record.descriptionContract}` : contract}
        </Link>
      ),
    },
    {
      title: t('contract_dashboard.position'),
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: t('contract_dashboard.subPosition'),
      dataIndex: 'subPosition',
      key: 'subPosition',
    },
    {
      title: t('contract_dashboard.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('contract_dashboard.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('contract_dashboard.mu'),
      dataIndex: 'mu',
      key: 'mu',
    },
    {
      title: t('contract_dashboard.cabecera'),
      dataIndex: 'companySap',
      key: 'companySap',
      render: (companySap: string) => companySap || '-',
    },

    {
      title: t('contract_dashboard.price'),
      dataIndex: 'price',
      key: 'price',
      render: (price: string) => <FormatPrice price={price} />,
    },
    {
      title: t('contract_dashboard.currency'),
      dataIndex: 'currency',
      key: 'currency',
    },
    {
      title: t('contract_dashboard.per'),
      dataIndex: 'per',
      key: 'per',
    },
    {
      title: t('contract_dashboard.service_number'),
      dataIndex: 'asnumId',
      key: 'asnumId',
      render: (asnumId: string) => (
        <Link to={getRoutePath(asnumId)} onClick={e => handleAsnumClick(asnumId, e)}>
          {asnumId}
        </Link>
      ),
    },
    {
      title: t('contract_dashboard.carrier'),
      key: 'businessName',
      dataIndex: 'carrier',
      render: (carrier: Carrier) => carrier && <>{carrier?.businessName}</>,
    },
    {
      title: t('contract_dashboard.observation'),
      dataIndex: 'observation',
      key: 'observation',
    },
    {
      title: t('contract_dashboard.date_validity_agreemen_from'),
      dataIndex: 'dateValidityAgreementFrom',
      key: 'dateValidityAgreementFrom',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD') : '-'),
    },
    {
      title: t('contract_dashboard.date_validity_agreemen_to'),
      dataIndex: 'dateValidityAgreementTo',
      key: 'dateValidityAgreementTo',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD') : '-'),
    },
  ]

  const handleAsnumFilterChange = (value: any) => {
    if (value) {
      setFilters((prevState: ContractFilters) => ({
        ...prevState,
        page: 1,
        asnum: value as string,
      }))
    } else {
      handleClearFilters()
    }
  }

  const handleClearFilters = () => {
    setFilters(initialContractsFiltersState.filters)
  }

  const handleCarrierFilterChange = (value: any) => {
    setFilters({
      ...initialContractsFiltersState.filters,
      carrierId: value as number,
    })
  }

  useEffect(() => {
    const service = searchParams.get('service')
    if (!service) {
      clearAllFilters()
    }
  }, [clearAllFilters, searchParams])

  useEffect(() => {
    const handlePopState = () => {
      const service = searchParams.get('service')
      if (!service) {
        clearAllFilters()
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [clearAllFilters, searchParams])

  return (
    <ContentContainer className="admin-dashboard">
      <StyledRow className="sticky-options border-botom">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <Tooltip placement="bottomRight" title={t('globals.generics.clean_filters')}>
              <StyledButton
                className="inline option"
                onClick={resetFilters}
                icon={<ClearOutlined style={{ fontSize: '18px' }} />}
              />
            </Tooltip>
            <StyledSelect
              maxTagCount="responsive"
              showSearch
              allowClear
              placeholder={t('admin_router.filter_options.by_carrier.title')}
              options={allCarriersOptions}
              filterOption={handleFilterOptions}
              onClear={handleClearFilters}
              value={filters?.carrierId}
              onChange={handleCarrierFilterChange}
            />
            <StyledSelect
              maxTagCount="responsive"
              showSearch
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.contract')}
              options={contractsFilters?.map(arrayToOptions())}
              filterOption={handleFilterOptions}
              suffixIcon={null}
              value={filters?.contract}
              onChange={handleContractFilterChange}
              loading={contractsFiltersIsLoading}
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.asnum')}
              showSearch
              options={asnums?.map(arrayToOptions())}
              onChange={handleAsnumFilterChange}
              value={filters?.asnum}
              loading={asnumsIsLoading}
              disabled={asnumsIsLoading}
            />
          </div>
        </div>
      </StyledRow>
      <Spin spinning={contractsIsLoading || isContractsSync}>
        <StyledRow className="data-table-container">
          <Table
            dataSource={contracts?.contracts}
            className="dataTable"
            columns={columns}
            pagination={pagination}
            showSorterTooltip={false}
            rowKey="id"
            scroll={{ x: true }}
            loading={contractsIsLoading}
            style={{ width: '100%', height: '100%' }}
          />
        </StyledRow>
      </Spin>
    </ContentContainer>
  )
}

export { ContractsDashboard }
